import { FormikErrors, useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { regularExpressions } from 'validations/regularExpressions'

import { actionTypes } from 'actions/actionTypes'
import { basketPostcodeChange } from 'actions/basket'
import { trackUTMAndPromoCode } from 'actions/tracking'
import { checkoutClickContinueToPayment } from 'actions/trackingKeys'
import { userProspect } from 'actions/user'
import { AddressFields } from 'routes/Checkout/Components/PostcodeAddressFields'
import { CheckoutStepIds } from 'routes/Checkout/checkoutConfig'
import { checkoutConstants } from 'routes/Checkout/checkoutConstants'
import { phoneValidator } from 'routes/Checkout/checkoutFormUtils'
import {
  getDeliveryAddress,
  getDeliveryInstruction,
  getName,
  getPhone,
} from 'routes/Checkout/checkoutSelectors'
import { getBasketPostcode } from 'selectors/basket'
import {
  getDeliveryDetailsInstructions,
  getDeliveryDetailsInstructionsCustom,
} from 'selectors/deliveryDetails'

type FormFields = {
  firstName: string
  lastName: string
  phone: string
  deliveryInstruction: string
  deliveryInstructionCustom: string
} & AddressFields

const NAME_MAX_LENGTH = 50
const POSTCODE_MIN_LENGTH = 5
const POSTCODE_MAX_LENGTH = 8
const HOUSE_NUMBER_MAX_LENGTH = 200
const STREET_MAX_LENGTH = 200
const TOWN_MAX_LENGTH = 200
const DELIVERY_INSTRUCTION_CUSTOM_MIN_LENGTH = 5
const DELIVERY_INSTRUCTION_CUSTOM_MAX_LENGTH = 32

const validate = (formValues: FormFields): FormikErrors<FormFields> => {
  const errors: FormikErrors<FormFields> = {}

  if (!formValues.firstName) {
    errors.firstName = 'First name is required'
  } else if (!regularExpressions.name.test(formValues.firstName)) {
    errors.firstName =
      "Please use only letters (a-z), hyphens (-), apostrophes (' and ‘) and European special characters."
  } else if (formValues.firstName.length > NAME_MAX_LENGTH) {
    errors.firstName = 'First name must be under 50 characters'
  }

  if (!formValues.lastName) {
    errors.lastName = 'Last name is required'
  } else if (!regularExpressions.name.test(formValues.lastName)) {
    errors.lastName =
      "Please use only letters (a-z), hyphens (-), apostrophes (' and ‘) and European special characters."
  } else if (formValues.lastName.length > NAME_MAX_LENGTH) {
    errors.lastName = 'Last name must be under 50 characters'
  }

  if (formValues.searchPostcode.length < POSTCODE_MIN_LENGTH) {
    errors.searchPostcode = 'Postcode must be at least 5 characters'
  } else if (formValues.searchPostcode.length > POSTCODE_MAX_LENGTH) {
    errors.searchPostcode = 'Postcode must be under 8 characters'
  }

  if (!formValues.houseNo) {
    errors.houseNo = 'House number is required'
  } else if (formValues.houseNo.length > HOUSE_NUMBER_MAX_LENGTH) {
    errors.houseNo = 'House number must be under 200 characters'
  }

  if (formValues.street.length > STREET_MAX_LENGTH) {
    errors.street = 'Street must be under 200 characters'
  }

  if (!formValues.town) {
    errors.town = 'City is required'
  } else if (formValues.town.length > TOWN_MAX_LENGTH) {
    errors.town = 'City must be under 200 characters'
  }

  const phoneError = phoneValidator(formValues.phone)
  if (phoneError) errors.phone = phoneError

  if (formValues.deliveryInstruction === checkoutConstants.leaveBoxOptions[0]) {
    errors.deliveryInstruction = 'Delivery instruction is required'
  }

  if (checkoutConstants.leaveBoxOptionsWithMessage.includes(formValues.deliveryInstruction)) {
    if (formValues.deliveryInstructionCustom.length < DELIVERY_INSTRUCTION_CUSTOM_MIN_LENGTH) {
      errors.deliveryInstructionCustom = 'Delivery instruction must be at least 5 characters'
    } else if (
      formValues.deliveryInstructionCustom.length > DELIVERY_INSTRUCTION_CUSTOM_MAX_LENGTH
    ) {
      errors.deliveryInstructionCustom = 'Delivery instruction must be under 32 characters'
    }
  }

  return errors
}

export const useDeliveryDetailsForm = (onStepChange: (stepId: CheckoutStepIds) => void) => {
  const dispatch = useDispatch()

  const { firstName, lastName } = useSelector(getName)
  const postcode = useSelector(getBasketPostcode)
  const deliveryAddress = useSelector(getDeliveryAddress)
  const phone = useSelector(getPhone)

  const deliveryInstructionsDeliveryDetails = useSelector(getDeliveryDetailsInstructions)
  const deliveryInstructionsCustomDeliveryDetails = useSelector(
    getDeliveryDetailsInstructionsCustom,
  )
  const {
    deliveryInstruction: deliveryInstructionCheckout,
    deliveryInstructionCustom: deliveryInstructionCustomCheckout,
  } = useSelector(getDeliveryInstruction)

  let deliveryInstruction
  let deliveryInstructionCustom

  // if deliveryDetails are present in delivery slice read from there
  // else read from checkout slice.
  // This will be cleaned up once the experiment completes.
  if (deliveryInstructionsDeliveryDetails || deliveryInstructionsCustomDeliveryDetails) {
    deliveryInstruction = deliveryInstructionsDeliveryDetails
    deliveryInstructionCustom = deliveryInstructionsCustomDeliveryDetails
  } else {
    deliveryInstruction = deliveryInstructionCheckout
    deliveryInstructionCustom = deliveryInstructionCustomCheckout
  }

  const saveFormFieldsToStore = (formValues: FormFields) => {
    dispatch({
      type: actionTypes.CHECKOUT_SET_NAME,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
    })
    dispatch({
      type: actionTypes.CHECKOUT_SET_DELIVERY_ADDRESS,
      postcode: formValues.postcode,
      houseNo: formValues.houseNo,
      street: formValues.street,
      town: formValues.town,
      county: formValues.county,
      udprn: formValues.udprn,
    })
    dispatch({
      type: actionTypes.CHECKOUT_SET_PHONE,
      phone: formValues.phone,
    })
    dispatch({
      type: actionTypes.CHECKOUT_SET_DELIVERY_INSTRUCTION,
      deliveryInstruction: formValues.deliveryInstruction,
      deliveryInstructionCustom: formValues.deliveryInstructionCustom,
    })
  }

  return useFormik<FormFields>({
    initialValues: {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      searchPostcode: postcode ?? '',
      postcode: postcode ?? '',
      houseNo: deliveryAddress?.get('houseNo') ?? '',
      street: deliveryAddress?.get('street') ?? '',
      town: deliveryAddress?.get('town') ?? '',
      county: deliveryAddress?.get('county') ?? '',
      udprn: deliveryAddress?.get('udprn') ?? '',
      phone: phone ?? '',
      deliveryInstruction: deliveryInstruction ?? checkoutConstants.leaveBoxOptions[0],
      deliveryInstructionCustom: deliveryInstructionCustom ?? '',
    },
    validateOnBlur: true,
    validateOnMount: true,
    validate,
    onSubmit: (formValues) => {
      saveFormFieldsToStore(formValues)
      dispatch(basketPostcodeChange(formValues.postcode))
      dispatch(trackUTMAndPromoCode(checkoutClickContinueToPayment))
      dispatch(userProspect())
      onStepChange(CheckoutStepIds.PAYMENT)
    },
  })
}
